import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { CalendarDays, X } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * Extracts a 4-digit year for display from a value that may be "YYYY"
 * (new format) or legacy "YYYY-MM". Returns null if empty/invalid.
 */
export function formatYear(value) {
  if (!value) return null;
  const y = String(value).slice(0, 4);
  return /^\d{4}$/.test(y) ? y : null;
}

/**
 * A year-only selector Popover. Stores value as a "YYYY" string.
 * Props:
 *  - value: "YYYY" (or legacy "YYYY-MM") or ""
 *  - onChange: (value: "YYYY" | "") => void
 *  - language: 'en' | 'ar'
 *  - placeholder: string
 *  - data-testid: string
 */
export default function YearPicker({ value, onChange, language = 'en', placeholder, 'data-testid': testId, className }) {
  const [open, setOpen] = useState(false);
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 26 }, (_, i) => currentYear - 10 + i); // -10 .. +15
  const cur = formatYear(value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          type="button"
          data-testid={testId}
          className={cn(
            'w-full justify-start text-left font-normal gap-2',
            !cur && 'text-muted-foreground',
            className
          )}
        >
          <CalendarDays className="w-4 h-4 shrink-0 opacity-60" />
          <span className="flex-1 truncate">
            {cur || (placeholder || (language === 'ar' ? 'اختر السنة' : 'Select year'))}
          </span>
          {value && (
            <X
              className="w-3.5 h-3.5 shrink-0 opacity-50 hover:opacity-100"
              onClick={(e) => { e.stopPropagation(); onChange(''); setOpen(false); }}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
          {language === 'ar' ? 'اختر السنة' : 'Select year'}
        </p>
        <div className="grid grid-cols-4 gap-1 max-h-56 overflow-y-auto">
          {years.map(y => {
            const sel = cur === String(y);
            return (
              <Button
                key={y}
                type="button"
                variant={sel ? 'default' : 'outline'}
                size="sm"
                className={cn('h-8 text-xs', sel && 'font-semibold')}
                onClick={() => { onChange(String(y)); setOpen(false); }}
                data-testid={testId ? `${testId}-year-${y}` : undefined}
              >
                {y}
              </Button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
